import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getRelatedPosts } from "@/content/blogPosts";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CalendarDays } from "lucide-react";
import heroImage from "@/assets/gallery/kid-whale-float.jpg";
import familyImg from "@/assets/tubing/family-tubing-fun.jpg";
import kidsImg from "@/assets/tubing/kid-tubing.jpg";
import cabanaImg from "@/assets/cabanas/kids-river-tubes.jpg";

const PUBLISH_DATE = "May 5, 2026";
const CANONICAL = "/blog/tubing-with-kids-guadalupe-river";
const TITLE =
  "Tubing With Kids on the Guadalupe River | Son's";
const DESCRIPTION =
  "Tubing with kids on the Guadalupe River near New Braunfels: best ages, life jackets, calm stretches, cabana rentals & what to pack — from Son's.";

const FAQS = [
  {
    q: "What age is best for tubing with kids on the Guadalupe River?",
    a: "Most families do great with kids ages 5 and up on the calm stretches we float. Younger kids can ride on a parent's lap or in a connected tube, and confident swimmers ages 8+ usually want their own tube with a properly fitted life jacket.",
  },
  {
    q: "Do kids need life jackets when tubing the Guadalupe?",
    a: "Yes — we strongly recommend a properly fitted life jacket for every kid on the river. You can bring your own or grab one on site.",
  },
  {
    q: "Is tubing the Guadalupe River safe for families?",
    a: "The stretches we book around at Son's Guadalupe are calm and family-friendly. Our team monitors river conditions daily and will let you know if it's a great float day or a stay-at-the-cabana day.",
  },
  {
    q: "Why book a cabana for a family tubing day?",
    a: "A cabana rental on the Guadalupe River gives you a shaded private basecamp with a picnic table, direct river access, free parking, and bathrooms close by — perfect for snacks, naps, and easy logistics with kids.",
  },
  {
    q: "What should we pack for tubing with kids?",
    a: "Life jackets, reef-safe sunscreen, water shoes, a waterproof bag with snacks and a dry shirt, towels, a small cooler, and a change of clothes for the ride home.",
  },
];

const schemaJson = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://sonsguadalupe.com/blog" },
        { "@type": "ListItem", position: 3, name: "Tubing With Kids on the Guadalupe River — A Parent's Guide", item: `https://sonsguadalupe.com/blog/tubing-with-kids-guadalupe-river` },
      ],
    },
    {
      "@type": "BlogPosting",
      headline: "Tubing With Kids on the Guadalupe River — A Parent's Guide",
      description: DESCRIPTION,
      image: "https://sonsguadalupe.com/og-image.png",
      author: { "@type": "Organization", name: "Son's Guadalupe" },
      datePublished: "2026-05-05",
      mainEntityOfPage: { "@type": "WebPage", "@id": `https://sonsguadalupe.com${CANONICAL}` },
      keywords:
        "tubing with kids on the Guadalupe River, family tubing near New Braunfels, kids tubing Guadalupe, family getaways near New Braunfels, cabana rentals on the Guadalupe River",
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
  ],
};

const BlogTubingWithKids = () => {
  const related = getRelatedPosts("tubing-with-kids-guadalupe-river", 3);
  return (
    <div className="min-h-screen">
      <SEOHead title={TITLE} description={DESCRIPTION} canonical={CANONICAL} schemaJson={schemaJson} />
      <Header />
      <main className="pt-24 pb-16">
        <div className="relative h-[55vh] min-h-[400px]">
          <img
            src={heroImage}
            alt="Child floating on the Guadalupe River near New Braunfels — tubing with kids"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="container-narrow mx-auto">
              <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
                <CalendarDays className="w-4 h-4" /> {PUBLISH_DATE}
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white max-w-4xl leading-tight">
                Tubing With Kids on the Guadalupe River — A Parent's Guide
              </h1>
            </div>
          </div>
        </div>

        <article className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-primary hover:underline mb-8">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>

            <div className="prose prose-lg max-w-none text-foreground space-y-6">
              <p className="text-xl leading-relaxed">
                Thinking about <strong>tubing with kids on the Guadalupe River</strong>? Good news — it's one of the most fun, family-friendly things to do near New Braunfels, and with the right setup it's surprisingly easy. This guide covers the best ages, safety, what to pack, and why a riverside cabana rental makes all the difference for a family tubing day.
              </p>

              <img src={familyImg} alt="Family tubing the Guadalupe River with kids near New Braunfels" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">What's the Best Age for Tubing the Guadalupe?</h2>
              <p>
                Most families do great with kids ages 5 and up on the calm stretches we float at Son's Guadalupe. Younger kids ride on a parent's lap or in a connected tube. Older kids (8+) often want their own tube — and our gentle current makes it manageable for confident swimmers wearing a life jacket.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Life Jackets, Safety & River Conditions</h2>
              <p>
                We strongly recommend a properly fitted life jacket for every kid on the river — bring your own or grab one on site. Our team monitors river conditions daily and will let you know if it's a great float day or a "stay at the cabana and swim" day. Tubing the Guadalupe River with kids is safest in the calm, family-friendly stretches we book around.
              </p>

              <img src={kidsImg} alt="Kid tubing on the calm Guadalupe River near New Braunfels" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Why a Cabana Changes Everything for Family Tubing</h2>
              <p>
                With young kids, the hardest part of tubing the Guadalupe isn't the float — it's the logistics. Where do you park? Where do you change? Where do you eat lunch and put the toddler down for a nap? <strong>Cabana rentals on the Guadalupe River</strong> solve all of it. You get a shaded private spot with a picnic table, direct river access, free parking, and bathrooms close by. Float, snack, nap, repeat.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">What to Pack for Tubing With Kids</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Life jackets for every kid (and adults who want them)</li>
                <li>Reef-safe sunscreen and rash guards</li>
                <li>Water shoes — the river bottom is rocky in spots</li>
                <li>A waterproof bag with snacks and an extra dry shirt</li>
                <li>Towels, a small cooler, and a change of clothes for the ride home</li>
                <li>Cash for any add-ons (we recommend booking tubes online in advance)</li>
              </ul>

              <img src={cabanaImg} alt="Kids playing in tubes outside a riverside cabana on the Guadalupe River" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">A Sample Family Tubing Day at Son's</h2>
              <p>
                Arrive mid-morning, check into your cabana, set up the picnic table, and let the kids splash near the cabana. Mid-day, do a single short float together. Back at the cabana for lunch, naps, and a slower afternoon swim. Add s'mores at the fire pit if you're staying overnight glamping or tent camping on River Road. That's a perfect Guadalupe River day with kids.
              </p>

              <h2 className="font-heading text-3xl font-bold text-foreground mt-10">Ready to Plan a Family Float?</h2>
              <p>
                Book a cabana, reserve your tubes, and let us handle the rest. Tubing with kids on the Guadalupe River is one of those Texas memories your family will talk about for years.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Family Tubing FAQs</h2>
              <div className="space-y-6">
                {FAQS.map((f) => (
                  <div key={f.q}>
                    <h3 className="font-heading text-lg font-semibold text-foreground">{f.q}</h3>
                    <p className="mt-2">{f.a}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Link to="/book-now"><Button variant="default" size="lg" className="text-lg px-10 py-6">Book a Cabana</Button></Link>
                <Link to="/tubing"><Button variant="outline" size="lg" className="text-lg px-10 py-6">Tubing Info</Button></Link>
              </div>
            </div>
          </div>
        </article>
        <RelatedReads posts={related} />

      </main>
      <SEOContentBlock
        eyebrow="More Family Resources"
        heading="Plan the Perfect Family Day on the Guadalupe"
        paragraphs={[
          "Pair tubing with kids on the Guadalupe River with a cabana rental on the Guadalupe River, glamping near New Braunfels, or tent camping on River Road. Son's Guadalupe makes family getaways near New Braunfels effortless — book everything in one place.",
        ]}
        links={[
          { label: "Cabanas", to: "/cabanas" },
          { label: "Tubing", to: "/tubing" },
          { label: "Family Getaways", to: "/family-getaways-new-braunfels" },
          { label: "Glamping", to: "/glamping" },
        ]}
      />
      <Footer />
    </div>
  );
};

export default BlogTubingWithKids;
